import {
  PhoneOutlined,
  UserOutlined,
  ShopOutlined,
  CalendarOutlined,
  ThunderboltFilled,
  ShoppingOutlined,
} from "@ant-design/icons";
import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import axios from "axios";

import styles from "./order.module.css";
import { getAccessToken, apiUrl } from "../../common/apis";
import { fetchRiderState } from "../../common/dutyStatus";
import { currentPosition } from "../../common/currentLocation";
import { describeDistance, dropCheck, pickupCheck } from "../../common/proximity";
import GoToHome from "../../Components/GoToHome";
import CustomPopup from "../../Components/Popup";
import MapButton from "../../Components/MapButton/MapButton";

function Order() {
  const location = useLocation();
  const navigate = useNavigate();
  const order = location.state?.order;
  const unassignedOrder = location.state?.unassignedOrder;
  const [deliveryPin, setDeliveryPin] = useState("");
  const [orderStatus, setOrderStatus] = useState(
    unassignedOrder ? 2 : (order?.status?.id || 4)
  );
  const [popupView, setPopupView] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showDeliveryModal, setShowDeliveryModal] = useState(false);
  const [checkingLocation, setCheckingLocation] = useState(false);
  const [nearCustomer, setNearCustomer] = useState(false);
  const [pinReason, setPinReason] = useState("");
  const [dialog, setDialog] = useState(null);
  const [cashLimit, setCashLimit] = useState({ limit: null, inHand: 0, reached: false });
  const pinInputRef = useRef(null);

  const canAccept = unassignedOrder || orderStatus === 2;
  const acceptBlocked = canAccept && cashLimit.reached;

  useEffect(() => {
    if (!canAccept) return;
    fetchRiderState()
      .then((state) => {
        if (!state) return;
        setCashLimit({
          limit: state.codLimit,
          inHand: state.cashInHand,
          reached: state.codLimitReached,
        });
      })
      .catch(() => {
        // The server enforces the limit anyway; the button only mirrors it.
      });
  }, [canAccept]);

  const getErrorMessage = (data, fallback = "An error occurred") => {
    if (!data) return fallback;
    if (typeof data === "string") return data;
    if (data.Error) return data.Error;
    if (data.error) return typeof data.error === "string" ? data.error : JSON.stringify(data.error);
    if (data.Message && data.Message !== "Exception error occured") return data.Message;
    if (data.message && data.message !== "Exception error occured") return data.message;
    if (data.detail) return data.detail;
    if (data.Message) return data.Message;
    if (data.message) return data.message;
    return fallback;
  };

  const isResponseSuccess = (data) => {
    if (!data) return false;
    if (
      data.Status === false ||
      data.Status === "False" ||
      data.Status === "false" ||
      data.status === false ||
      data.status === "False" ||
      data.status === "false"
    ) {
      return false;
    }
    if (data.Succeeded === false || data.succeeded === false) {
      return false;
    }
    if (data.Success === false || data.success === false) {
      return false;
    }
    if (data.Error || data.error) {
      return false;
    }
    return true;
  };

  const assignOrder = () => {
    setIsLoading(true);
    let data = new FormData();
    data.append("order_id", order?.id);
    data.append("keyword", "open_orders");

    let config = {
      method: "post",
      url: `${apiUrl}delivery/orders/`,
      headers: { Authorization: getAccessToken() },
      data: data,
    };

    axios(config)
      .then((res) => {
        setIsLoading(false);
        const resData = res.data;
        if (!isResponseSuccess(resData)) {
          toast.error(getErrorMessage(resData, "Failed to accept order"));
        } else {

          // Update local cache: Add to assignedOrders, remove from unassignedOrders
          try {
            const rawAssigned = sessionStorage.getItem("assignedOrders");
            const existingAssigned = rawAssigned ? JSON.parse(rawAssigned) : [];
            const updatedOrder = {
              ...order,
              status: { id: 4, name: "assigned" },
              delivery_boy: true,
            };
            const newAssigned = [
              updatedOrder,
              ...existingAssigned.filter((o) => o.id !== order.id),
            ];
            sessionStorage.setItem("assignedOrders", JSON.stringify(newAssigned));

            const rawUnassigned = sessionStorage.getItem("unassignedOrders");
            if (rawUnassigned) {
              const existingUnassigned = JSON.parse(rawUnassigned);
              const newUnassigned = existingUnassigned.filter(
                (o) => o.id !== order.id
              );
              sessionStorage.setItem(
                "unassignedOrders",
                JSON.stringify(newUnassigned)
              );
            }
          } catch (e) {
            console.error("Cache update error:", e);
          }

          navigate("/", { state: { defaultTab: "accepted" } });
        }
      })
      .catch((err) => {
        setIsLoading(false);
        const resData = err.response?.data;
        toast.error(getErrorMessage(resData, "Failed to accept order"));
      });
  };

  useEffect(() => {
    if (!showDeliveryModal) return;
    const timer = setTimeout(() => {
      pinInputRef.current?.scrollIntoView({
        block: "center",
        behavior: "smooth",
      });
    }, 350);
    return () => clearTimeout(timer);
  }, [showDeliveryModal]);

  const changeOrderStatus = (orderStatus, orderPin) => {
    setIsLoading(true);
    if (orderStatus === 4) {
      let data = new FormData();
      data.append("keyword", "change_status");
      data.append("order_id", order?.id);
      data.append("status_id", "5");
      data.append("description", "out for delivery");

      let config = {
        method: "patch",
        url: `${apiUrl}order/details/`,
        headers: { Authorization: getAccessToken() },
        data: data,
      };

      axios(config)
        .then((res) => {
          setIsLoading(false);
          const resData = res.data;
          if (!isResponseSuccess(resData)) {
            toast.error(getErrorMessage(resData, "Failed to update status"));
          } else {
            setOrderStatus(5);
            sessionStorage.setItem(
              "orderDetails",
              JSON.stringify({ order: order?.id, status: 5 })
            );

            // Update status in cached assignedOrders
            try {
              const rawAssigned = sessionStorage.getItem("assignedOrders");
              if (rawAssigned) {
                const list = JSON.parse(rawAssigned);
                const updatedList = list.map((o) =>
                  o.id === order?.id ? { ...o, status: { id: 5, name: "out for delivery" } } : o
                );
                sessionStorage.setItem("assignedOrders", JSON.stringify(updatedList));
              }
            } catch (e) {}

            toast.success(resData?.Message || resData?.message || "Out for delivery");
          }
        })
        .catch((err) => {
          setIsLoading(false);
          const resData = err.response?.data;
          toast.error(getErrorMessage(resData, "Failed to update status"));
        });
    } else if (orderStatus === 5) {
      const pin = nearCustomer ? String(orderPin || "").trim() : deliveryPin.trim();
      if (!pin) {
        setIsLoading(false);
        toast.warning("Please enter the customer delivery PIN");
        return;
      }
      if (nearCustomer || String(orderPin).trim() === pin) {
        let data = new FormData();
        data.append("keyword", "change_status");
        data.append("order_id", Number(order?.id));
        data.append("description", "order delivered");
        data.append("status_id", "6");
        data.append("pin", pin);

        let config = {
          method: "patch",
          url: `${apiUrl}order/details/`,
          headers: { Authorization: getAccessToken() },
          data: data,
        };

        axios(config)
          .then((res) => {
            setIsLoading(false);
            const resData = res.data;
            if (!isResponseSuccess(resData)) {
              toast.error(getErrorMessage(resData, "Failed to deliver order"));
            } else {
              toast.success(resData?.Message || resData?.message || "Order delivered successfully");
              setShowDeliveryModal(false);

              // Remove delivered order from assignedOrders cache
              try {
                const rawAssigned = sessionStorage.getItem("assignedOrders");
                if (rawAssigned) {
                  const list = JSON.parse(rawAssigned);
                  const updatedList = list.filter((o) => o.id !== order?.id);
                  sessionStorage.setItem("assignedOrders", JSON.stringify(updatedList));
                }
              } catch (e) {}

              navigate("/", { state: { defaultTab: "accepted" } });
            }
          })
          .catch((err) => {
            setIsLoading(false);
            const resData = err.response?.data;
            toast.error(getErrorMessage(resData, "Failed to deliver order"));
          });
      } else {
        setIsLoading(false);
        toast.error("Incorrect delivery PIN. Please ask customer for the correct PIN.");
      }
    }
  };

  const closeDialog = () => setDialog(null);

  const askAgain = (title, message) =>
    setDialog({
      title,
      message,
      actions: [
        { label: "Retry", primary: true, onClick: () => startPickup() },
        { label: "Close", onClick: closeDialog },
      ],
    });

  const startPickup = async () => {
    const restaurant = order?.branch?.name || "the restaurant";
    setDialog(null);
    setCheckingLocation(true);
    const here = await currentPosition();
    setCheckingLocation(false);

    if (here.error === "permission") {
      askAgain(
        "Allow location",
        `Foodoor needs your location to confirm you are at ${restaurant}. Allow location for this site in your browser settings, then retry.`
      );
      return;
    }
    if (here.error) {
      askAgain(
        "Location not found",
        "We couldn't find your location. Turn on location, step outside and retry. If it still fails, tell the office."
      );
      return;
    }

    const check = pickupCheck(here.point, order?.branch);
    if (check.reason === "no_restaurant_location") {
      setDialog({
        title: "Restaurant location missing",
        message: "Tell the office to mark this order picked up.",
        actions: [{ label: "Close", onClick: closeDialog }],
      });
      return;
    }
    if (!check.allowed) {
      askAgain(
        "Not at the restaurant yet",
        `You are ${describeDistance(check.distance)} from ${restaurant}. Move closer and retry. If you are already there, tell the office.`
      );
      return;
    }

    setDialog({
      title: "Out for delivery?",
      message: `Confirm you have collected order FDR ${order?.id} from ${restaurant}.`,
      actions: [
        {
          label: "Confirm",
          primary: true,
          onClick: () => {
            closeDialog();
            changeOrderStatus(4);
          },
        },
        { label: "Cancel", onClick: closeDialog },
      ],
    });
  };

  const startDelivery = async () => {
    setCheckingLocation(true);
    const here = await currentPosition();
    setCheckingLocation(false);

    const check = here.error
      ? { withoutPin: false }
      : dropCheck(here.point, here.accuracy, order?.address);
    const pinKnown = String(order?.user?.details?.order_pin || "").trim() !== "";

    setNearCustomer(check.withoutPin && pinKnown);
    setPinReason(
      check.distance !== undefined && !check.withoutPin
        ? `You are ${describeDistance(check.distance)} from the customer's location, so ask for the PIN.`
        : check.withoutPin
          ? ""
          : "Your location could not be confirmed, so ask for the PIN."
    );
    setDeliveryPin("");
    setShowDeliveryModal(true);
  };

  const getOrderTime = (time) => {
    if (!time) return "";
    const utcDateTime = new Date(time);
    const istDateTime = utcDateTime
      .toLocaleString("en-IN", {
        timeZone: "Asia/Kolkata",
        day: "numeric",
        month: "short",
        hour: "2-digit",
        minute: "2-digit",
      });
    return istDateTime;
  };

  useEffect(() => {
    if (unassignedOrder) {
      setOrderStatus(2);
      return;
    }
    let localOrder = JSON.parse(sessionStorage.getItem("orderDetails"));
    if (order?.id === localOrder?.order) {
      setOrderStatus(localOrder.status);
    } else if (order?.status?.id) {
      setOrderStatus(order.status.id);
    }
  }, [order, unassignedOrder]);

  useEffect(() => {
    if (popupView) {
      setTimeout(() => {
        setPopupView(false);
      }, 2000);
    }
  }, [popupView]);

  return (
    <div>
      <GoToHome />
      {popupView && <CustomPopup message={"Wrong pin entered"} />}
      {order && (
        <div className={styles.orderPageWrapper}>
          {/* Header */}
          <div className={styles.topOrderHeader}>
            <h2 className={styles.orderTitle}>Order Details</h2>
            <span className={styles.orderIdBadge}>{`#FDR ${order.id}`}</span>
          </div>

          {/* Customer Details Card */}
          <div className={styles.orderCard}>
            <div className={styles.cardSectionHeader}>
              <div className={styles.sectionTitleGroup}>
                <div className={styles.sectionIconWrapper}>
                  <UserOutlined />
                </div>
                <div>
                  <h4>{order.user?.first_name || "Customer"}</h4>
                  <p>
                    <CalendarOutlined style={{ marginRight: "4px" }} />
                    {getOrderTime(order.payment?.date || order.date)}
                  </p>
                </div>
              </div>

              {!unassignedOrder && order.user?.details?.mobile && (
                <a
                  href={`tel:${order.user.details.mobile}`}
                  className={styles.callActionBtn}
                >
                  <PhoneOutlined />
                  <span>Call</span>
                </a>
              )}
            </div>

            <div className={styles.detailRow}>
              <strong>Address:</strong>
              <span>{order.address?.address1 || "Not specified"}</span>
            </div>

            {order.address?.landmark && (
              <div className={styles.detailRow}>
                <strong>Landmark:</strong>
                <span>{order.address.landmark}</span>
              </div>
            )}

            {order.user?.mobile && (
              <div className={styles.detailRow}>
                <strong>Phone:</strong>
                <span>{order.user.mobile}</span>
              </div>
            )}

            {order.address?.latitude && order.address?.longitude && (
              <div style={{ marginTop: "12px" }}>
                <MapButton
                  latitude={order.address.latitude}
                  longitude={order.address.longitude}
                />
              </div>
            )}
          </div>

          {/* Delivery Tip Card */}
          {Number(order.delivery_boy_tip) > 0 && (
            <div className={styles.tipCard}>
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <ThunderboltFilled style={{ fontSize: "20px", color: "#16a34a" }} />
                <div>
                  <h5 style={{ margin: 0, fontSize: "14px", fontWeight: "700", color: "#15803d" }}>
                    Customer Tip Included
                  </h5>
                  <p style={{ margin: 0, fontSize: "12px", color: "#166534" }}>
                    Added directly to your payout
                  </p>
                </div>
              </div>
              <h3 className={styles.tipAmount}>₹{order.delivery_boy_tip}</h3>
            </div>
          )}

          {/* Delivery Note */}
          {order.deliveryNote && (
            <div className="delivery-note-wrapper">
              <h4>
                Delivery Note: <span>{order.deliveryNote}</span>
              </h4>
            </div>
          )}

          {/* Order Items Card */}
          {!unassignedOrder && order.issued_items && order.issued_items.length > 0 && (
            <div className={styles.orderCard}>
              <div className={styles.cardSectionHeader}>
                <div className={styles.sectionTitleGroup}>
                  <div className={`${styles.sectionIconWrapper} ${styles.sectionIconGreen}`}>
                    <ShoppingOutlined />
                  </div>
                  <div>
                    <h4>Order Items</h4>
                    <p>{`${order.issued_items.length} item(s) to deliver`}</p>
                  </div>
                </div>
              </div>

              <div className={styles.itemsList}>
                {order.issued_items.map((item, index) => (
                  <div key={index} className={styles.itemRow}>
                    <div>
                      <h5 className={styles.itemName}>
                        {`${index + 1}. ${item.item?.name || "Item"}`}
                      </h5>
                      <p className={styles.itemVariant}>
                        {`${item.item_varient?.name || ""} (₹${item.rate}) × ${item.qty}`}
                      </p>
                    </div>
                    <span className={styles.itemPrice}>₹{item.total}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Restaurant Card */}
          <div className={styles.orderCard}>
            <div className={styles.cardSectionHeader}>
              <div className={styles.sectionTitleGroup}>
                <div className={styles.sectionIconWrapper}>
                  <ShopOutlined />
                </div>
                <div>
                  <h4>{order.branch?.name || "Restaurant"}</h4>
                  <p>Pickup Location</p>
                </div>
              </div>

              {order.food_ready_at && <span className={styles.foodReadyTag}>✓ Food ready</span>}

              {order.branch?.mobile && (
                <a
                  href={`tel:${order.branch.mobile}`}
                  className={styles.callActionBtn}
                >
                  <PhoneOutlined />
                  <span>Call Store</span>
                </a>
              )}
            </div>

            <div className={styles.detailRow}>
              <strong>Address:</strong>
              <span>{order.branch?.address || "Store Address"}</span>
            </div>

            {order.branch?.mobile && (
              <div className={styles.detailRow}>
                <strong>Mobile:</strong>
                <span>{order.branch.mobile}</span>
              </div>
            )}
          </div>

          {/* Sticky Bottom Action Container */}
          <div className={styles.stickyBottomBar}>
            {canAccept ? (
              <>
                {acceptBlocked && (
                  <p className={styles.cashLimitNotice}>
                    You hold ₹{Math.round(cashLimit.inHand)} of your ₹
                    {Math.round(cashLimit.limit)} cash limit. Submit cash to accept new
                    orders.
                  </p>
                )}
                <button
                  onClick={() => assignOrder()}
                  className="order-status-btn"
                  disabled={isLoading || acceptBlocked}
                >
                  {isLoading
                    ? "Accepting..."
                    : acceptBlocked
                      ? "Cash limit reached"
                      : "Accept Order"}
                </button>
              </>
            ) : orderStatus === 4 ? (
              <button
                onClick={startPickup}
                className="order-status-btn"
                disabled={isLoading || checkingLocation}
              >
                {checkingLocation ? "Checking location..." : isLoading ? "Updating..." : "Out for Delivery"}
              </button>
            ) : (
              <button
                onClick={startDelivery}
                className="order-status-btn"
                disabled={isLoading || checkingLocation}
              >
                {checkingLocation ? "Checking location..." : "Complete Delivery"}
              </button>
            )}
          </div>

          {/* Delivery Completion Modal */}
          {showDeliveryModal && (
            <div
              className={styles.modalOverlay}
              onClick={() => !isLoading && setShowDeliveryModal(false)}
            >
              <div
                className={styles.modalContent}
                onClick={(e) => e.stopPropagation()}
              >
                <button
                  className={styles.modalCloseBtn}
                  onClick={() => !isLoading && setShowDeliveryModal(false)}
                  aria-label="Close"
                >
                  &times;
                </button>
                <h3 className={styles.modalTitle}>Complete Delivery</h3>
                <p className={styles.modalSubtitle}>{`Order FDR ${order.id}`}</p>

                <div className={styles.amountBox}>
                  <span className={styles.amountLabel}>Amount to Collect</span>
                  <h2 className={styles.amountValue}>
                    ₹{order.order_type === "COD" ? (order.actual_amount_collected || 0) : 0}
                  </h2>
                  <span className={styles.paymentTypeTag}>
                    {order.order_type === "COD" ? "Cash on Delivery (COD)" : "Paid Online"}
                  </span>
                </div>

                {nearCustomer ? (
                  <div className={styles.pinSection}>
                    <p className={styles.pinLabel}>You are at the customer's location. No PIN needed.</p>
                  </div>
                ) : (
                  <div className={styles.pinSection}>
                    <label htmlFor="modalDeliveryPin" className={styles.pinLabel}>
                      Customer Delivery PIN
                    </label>
                    {pinReason && <p className={styles.pinReason}>{pinReason}</p>}
                    <input
                      id="modalDeliveryPin"
                      ref={pinInputRef}
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      autoComplete="one-time-code"
                      maxLength={6}
                      value={deliveryPin}
                      className={styles.modalPinInput}
                      onChange={(e) =>
                        setDeliveryPin(e.target.value.replace(/\D/g, ""))
                      }
                      placeholder="Enter PIN"
                      autoFocus
                    />
                  </div>
                )}

                <div className={styles.modalActions}>
                  <button
                    onClick={() =>
                      changeOrderStatus(5, order?.user?.details?.order_pin)
                    }
                    className={styles.modalSubmitBtn}
                    disabled={isLoading || (!nearCustomer && !deliveryPin.trim())}
                  >
                    {isLoading ? "Verifying..." : "Confirm & Deliver"}
                  </button>
                  <button
                    onClick={() => !isLoading && setShowDeliveryModal(false)}
                    className={styles.modalCancelBtn}
                    disabled={isLoading}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}

          {dialog && (
            <div className={styles.modalOverlay} onClick={closeDialog}>
              <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
                <h3 className={styles.modalTitle}>{dialog.title}</h3>
                <p className={styles.modalSubtitle}>{dialog.message}</p>
                <div className={styles.modalActions}>
                  {dialog.actions.map((action) => (
                    <button
                      key={action.label}
                      onClick={action.onClick}
                      className={action.primary ? styles.modalSubmitBtn : styles.modalCancelBtn}
                    >
                      {action.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default Order;
